"use client";

import { Ico16Check } from "@/shared/assets/icons/ico_16_check";
import { Ico24ArrowRight } from "@/shared/assets/icons/ico_24_arrow_right";
import { useBreakpoint } from "@/shared/hooks/use-breakpoint";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/ui/accordion";
import { Button } from "@/shared/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { useTranslations } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

const faqList = [
  {
    id: 1,
    question: "f_me_faq_q_1",
    answer: "f_me_faq_a_1",
  },
  {
    id: 2,
    question: "f_me_faq_q_2",
    answer: "f_me_faq_a_2",
  },
  {
    id: 3,
    question: "f_me_faq_q_3",
    answer: "f_me_faq_a_3",
  },
  {
    id: 4,
    question: "f_me_faq_q_4",
    answer: "f_me_faq_a_4",
  },
  {
    id: 5,
    question: "f_me_faq_q_5",
    answer: "f_me_faq_a_5",
  },
];

export function PlanContent({ isLoggedIn }: { isLoggedIn: boolean }) {
  const t = useTranslations();
  const { isMobile } = useBreakpoint();

  return (
    <div className="pc:mt-[94px] tb:mt-20 tb:pt-20 pc:pb-40 tb:pb-30 mt-12 flex w-full flex-col items-center justify-center pt-15 pb-20">
      <div className="w-full max-w-[1280px]">
        <h1 className="heading-1 tb:mb-10 pc:px-0 tb:px-10 mb-5 px-4 text-gray-900">{"Plan"}</h1>
      </div>
      <div className="tb:mb-30 mb-20 w-full">
        <Tabs defaultValue="mps" className="w-full items-center gap-0">
          <div className="tb:mb-20 mb-15 flex w-full items-center justify-center border-b border-gray-100">
            <TabsList className="h-full w-full max-w-[1280px] p-0">
              <TabsTrigger
                className="body-xl-medium tb:py-7 rounded-none bg-white p-0 py-3 data-[state=active]:border-b-2 data-[state=active]:border-gray-900 data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-gray-900"
                value="mps"
              >
                {isMobile ? "MPS" : t("mn_mi_tab_mps")}
              </TabsTrigger>
              <TabsTrigger
                className="body-xl-medium tb:py-7 rounded-none bg-white p-0 py-3 data-[state=active]:border-b-2 data-[state=active]:border-gray-900 data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-gray-900"
                value="mds"
              >
                {isMobile ? "MDS" : t("mn_mi_tab_mds")}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="mps" className="pc:w-auto w-full">
            <div className="pc:flex-row pc:gap-5 pc:px-0 tb:px-10 flex w-full flex-col justify-center gap-6 px-4">
              <PlanCard productType="MPS" isLoggedIn={isLoggedIn} />
              <PlanCard productType="MPS" isYearly isLoggedIn={isLoggedIn} />
            </div>
          </TabsContent>
          <TabsContent value="mds" className="pc:w-auto w-full">
            <div className="pc:flex-row pc:gap-5 pc:px-0 tb:px-10 flex w-full flex-col justify-center gap-6 px-4">
              <PlanCard productType="MDS" isLoggedIn={isLoggedIn} />
              <PlanCard productType="MDS" isYearly isLoggedIn={isLoggedIn} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <div className="tb:px-10 pc:px-4 flex w-full max-w-[1280px] px-4">
        <Accordion
          type="multiple"
          className="tb:space-y-3 w-full space-y-2"
          defaultValue={["item-1", "item-2", "item-3", "item-4", "item-5"]}
        >
          {faqList.map((item) => (
            <AccordionItem value={`item-${item.id}`} className="border-b-0" key={item.id}>
              <AccordionTrigger className="bg-secondary-100 tb:px-6 tb:gap-4 tb:h-22 tb:py-0 tb:rounded-2xl flex items-center gap-3 rounded-[8px] px-4 py-4 data-[state=open]:rounded-b-none">
                <div className="flex items-center">
                  <div className="bg-secondary-200 body-lg-bold tb:size-10 tb:mr-4 mr-3 flex size-7.5 flex-shrink-0 items-center justify-center rounded-full text-gray-900">
                    Q
                  </div>
                  <span className="body-lg-bold text-gray-900">{t(item.question)}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="body-md-regular bg-secondary-100 tb:rounded-b-2xl tb:p-6 flex flex-col rounded-b-[8px] px-5 pt-1 text-gray-700">
                <p>{t(item.answer)}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}

interface PlanCardProps {
  productType: "MPS" | "MDS";
  isYearly?: boolean;
  isLoggedIn: boolean;
}

function PlanCard({ productType, isYearly = false, isLoggedIn }: PlanCardProps) {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const features = [`mn_mi_mps_ye_con_1`, `mn_mi_mps_ye_con_2`, `mn_mi_mps_ye_con_3`, `mn_mi_mps_ye_con_4`];

  return (
    <div className={`pc:w-[430px] relative w-full ${isYearly ? "border-primary-500" : ""}`}>
      {isYearly && (
        <div className="rounded-60 bg-primary-500 body-sm-bold absolute top-[-10px] right-[30px] animate-bounce px-3 py-[6px] text-white">
          {t("mn_mi_mps_ye_sale")}
        </div>
      )}
      <div
        className={`tb:rounded-t-24 tb:p-[30px] rounded-t-[16px] border bg-white p-6 ${isYearly ? "border-primary-500 border-[1.5px] border-b-0" : "border-gray-100"} ${productType === "MDS" ? "plan-gradient" : "bg-white"}`}
      >
        <div
          className={`rounded-6 tb:rounded-8 bg-primary-100 text-primary-500 body-sm-medium tb:px-3 tb:py-[6px] tb:mb-2 mb-[6px] w-fit px-[10px] py-1 ${productType === "MDS" ? "text-primary-500 bg-white" : "bg-primary-100 text-primary-500"}`}
        >
          {productType}
        </div>
        <div className={`tb:gap-1 flex flex-col gap-[2px] ${isYearly ? "mb-[25px]" : "tb:mb-[50px] mb-8"}`}>
          <span className="heading-4-bold text-gray-900">{isYearly ? t("mn_mi_mps_ye") : t("mn_mi_mps_mo")}</span>
          <span className="body-lg-regular text-gray-700">
            {isYearly ? (
              <>
                {t("mn_mi_mps_ye_sub_1")}{" "}
                <span className="text-primary-700 body-lg-bold">{t("mn_mi_mps_ye_sub_2")}</span>{" "}
                {t("mn_mi_mps_ye_sub_3")}
              </>
            ) : (
              t("mn_mi_mds_mo_sub")
            )}
          </span>
        </div>

        <div className="tb:mb-10 mb-5 flex flex-col gap-[2px]">
          {isYearly && (
            <span className="body-lg-bold text-red-error">
              15% <span className="body-lg-regular ml-2 text-gray-300 line-through">{t("mn_mi_mds_ye_bpr_2")}</span>
            </span>
          )}
          <span className="heading-2 text-gray-900">{isYearly ? t("mn_mi_mds_ye_pri") : t("mn_mi_mds_mo_pri")}</span>
          <span className="body-lg-regular text-gray-900">
            / {isYearly ? t("mn_mi_mds_ye_pri_sub") : t("mn_mi_mds_mo_pri_sub")}
          </span>
        </div>

        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => {
            if (isLoggedIn) {
              const params = new URLSearchParams({
                product: productType,
                plan: isYearly ? "1y" : "1m",
              });
              router.push(`/payment?${params.toString()}`);
            } else {
              const currentUrl = `${pathname}?${searchParams?.toString()}`;
              router.push(`/login?returnUrl=${encodeURIComponent(currentUrl)}`);
            }
          }}
        >
          {t("mn_mi_mds_mo_btn")} <Ico24ArrowRight className="tb:size-6 size-5" />
        </Button>
      </div>
      <div
        className={`tb:rounded-b-24 bg-secondary-100 tb:p-[30px] rounded-b-[16px] border p-6 ${isYearly ? "border-primary-500 border-[1.5px] border-t-1 border-t-gray-100" : "border-t-0 border-gray-100"}`}
      >
        <div className="flex flex-col gap-1">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-1">
              <Ico16Check className="tb:size-4 mt-[5px] size-[14px] shrink-0" />
              <span className="body-md-regular text-gray-500">{t(feature, { productType })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
